import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, ArrowLeftToLine } from "lucide-react";
import { calcVolumeForDose } from "@/data/drugs";
import { SyringeVial } from "@/components/visuals/SyringeVial";

const fmt = (n, d = 2) =>
  n == null || isNaN(n) ? "—" : Number(n).toFixed(d).replace(/\.?0+$/, "");

// Volume needs more precision than a 2-dp display for small draws.
const fmtVol = (v) => {
  if (v == null || isNaN(v)) return "—";
  if (v < 0.1) return v.toFixed(3);
  if (v < 1) return v.toFixed(2);
  return v.toFixed(2);
};

const Given = ({ label, value }) => (
  <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5">
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-mono-clinical text-sm text-slate-900 font-semibold leading-tight mt-0.5">
      {value}
    </div>
  </div>
);

const StatusBadge = ({ ok }) =>
  ok ? (
    <span className="flex items-center gap-1 text-emerald-600 text-xs font-bold">
      <CheckCircle2 className="w-4 h-4" /> Correct
    </span>
  ) : (
    <span className="flex items-center gap-1 text-red-600 text-xs font-bold">
      <XCircle className="w-4 h-4" /> Check again
    </span>
  );

export const WorksheetRow = ({ sel, drug, species, weight, result, answer, onAnswer, showWorking, calcValue }) => {
  const range = species === "cat" ? drug.doseCat : drug.doseDog;
  const concDisplay = drug.isMicrogram
    ? `${drug.concentration} mg/mL (${drug.concentration * 1000} mcg/mL)`
    : `${drug.concentration} mg/mL`;
  const correct = calcVolumeForDose(drug, parseFloat(weight) || 0, sel.dose);
  const borderClass = result
    ? result.ok
      ? "border-emerald-300 bg-emerald-50/40"
      : "border-red-300 bg-red-50/40"
    : "border-slate-200";

  return (
    <div
      className={`border-2 rounded-md p-4 transition-colors ${borderClass}`}
      data-testid={`worksheet-row-${sel.id}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="font-semibold text-slate-900">{drug.name}</div>
          <div className="text-xs text-slate-500">{drug.route}</div>
        </div>
        {result && <StatusBadge ok={result.ok} />}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <Given label="Ordered dose" value={`${fmt(sel.dose, 3)} ${range.unit}`} />
        <Given label="Weight" value={`${fmt(weight, 1)} kg`} />
        <Given label="Concentration" value={concDisplay} />
      </div>

      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <label htmlFor={`ans-${sel.id}`} className="text-sm font-semibold text-slate-700">
          Volume to give:
        </label>
        <div className="flex items-center gap-1.5">
          <Input
            id={`ans-${sel.id}`}
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={answer}
            onChange={(e) => onAnswer(sel.id, e.target.value)}
            data-testid={`answer-input-${sel.id}`}
            className="w-28 font-mono-clinical"
          />
          <span className="text-sm text-slate-500">mL</span>
        </div>
        <button
          type="button"
          onClick={() => onAnswer(sel.id, calcValue)}
          disabled={calcValue == null || calcValue === "Error" || isNaN(parseFloat(calcValue)) || parseFloat(calcValue) < 0}
          data-testid={`use-calc-result-${sel.id}`}
          className="flex items-center gap-1 text-xs font-semibold text-[#0047AB] border border-[#0047AB]/30 rounded-md px-2 py-1.5 hover:bg-[#0047AB]/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Drop the calculator result into this field"
        >
          <ArrowLeftToLine className="w-3.5 h-3.5" />
          Use calc result
          {calcValue != null && calcValue !== "Error" && !isNaN(parseFloat(calcValue)) && parseFloat(calcValue) >= 0 && (
            <span className="font-mono-clinical text-[#0047AB]/70">({calcValue})</span>
          )}
        </button>
        {result && !result.ok && (
          <span
            className="text-xs font-mono-clinical text-red-600"
            data-testid={`correct-answer-${sel.id}`}
          >
            Correct: {fmtVol(correct)} mL
          </span>
        )}
      </div>

      <div className="mt-3">
        <SyringeVial volumeMl={parseFloat(answer)} correctMl={correct} revealed={!!result} concLabel={concDisplay} />
      </div>

      {showWorking && (
        <div
          className="mt-2 text-[11px] text-slate-500 font-mono-clinical"
          data-testid={`working-${sel.id}`}
        >
          ({fmt(sel.dose, 3)} {range.unit.split("/")[0]}/kg × {fmt(weight, 1)} kg)
          {drug.isMicrogram ? " ÷ 1000 " : " "}÷ {drug.concentration} mg/mL ={" "}
          <span className="text-[#0047AB] font-semibold">{fmtVol(correct)} mL</span>
        </div>
      )}
    </div>
  );
};
